import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Eye, EyeOff, Star } from "lucide-react";
import PasswordStrength from "@/components/PasswordStrength";
import PasswordGenerator from "@/components/PasswordGenerator";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export const CATEGORIES = ["Personal", "Work", "Banking", "Email", "Social", "Shopping", "Entertainment", "Other"];

const empty = {
  type: "login",
  title: "",
  username: "",
  url: "",
  password: "",
  notes: "",
  category: "Personal",
  tags: [],
  is_favorite: false,
};

export default function VaultItemDialog({ open, onOpenChange, item, onSaved }) {
  const [form, setForm] = useState(empty);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (item) setForm({ ...empty, ...item, tags: item.tags || [] });
    else setForm(empty);
    setShowPw(false);
    setTagInput("");
  }, [item, open]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (form.tags.includes(t)) return;
    update({ tags: [...form.tags, t] });
    setTagInput("");
  };

  const removeTag = (t) => update({ tags: form.tags.filter((x) => x !== t) });

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type: form.type || "login",
        title: form.title.trim(),
        username: form.username || "",
        url: form.url || "",
        password: form.password || "",
        notes: form.notes || "",
        category: form.category || "Personal",
        tags: form.tags || [],
        is_favorite: !!form.is_favorite,
      };
      if (item?.id) {
        const { data } = await api.put(`/vault/${item.id}`, payload);
        toast.success("Item updated");
        onSaved?.(data);
      } else {
        const { data } = await api.post("/vault", payload);
        toast.success("Item saved to vault");
        onSaved?.(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-xl">
            {item?.id ? "Edit vault item" : "Add new item"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {item?.id ? "Update this vault entry." : "Create a new encrypted vault entry."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="e.g. Gmail — Personal"
              className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
              data-testid="vault-title-input"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Username / Email</Label>
              <Input
                value={form.username}
                onChange={(e) => update({ username: e.target.value })}
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
                data-testid="vault-username-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Website</Label>
              <Input
                value={form.url}
                onChange={(e) => update({ url: e.target.value })}
                placeholder="https://"
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1"
                data-testid="vault-url-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Password</Label>
            <div className="relative mt-1">
              <Input
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={(e) => update({ password: e.target.value })}
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] pr-10 font-mono"
                data-testid="vault-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-[color:var(--vault-text-tertiary)] hover:text-[color:var(--vault-brand)]"
                data-testid="toggle-show-password-btn"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="flex-1">
                <PasswordStrength value={form.password} />
              </div>
              <PasswordGenerator onUse={(pw) => update({ password: pw })} triggerLabel="Generate" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Category</Label>
              <Select value={form.category} onValueChange={(v) => update({ category: v })}>
                <SelectTrigger className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1" data-testid="vault-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end justify-end gap-3">
              <Label htmlFor="favorite-switch" className="text-sm text-[color:var(--vault-text-secondary)] flex items-center gap-2">
                <Star size={14} /> Favorite
              </Label>
              <Switch
                id="favorite-switch"
                checked={!!form.is_favorite}
                onCheckedChange={(v) => update({ is_favorite: v })}
                data-testid="vault-favorite-switch"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Tags</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {form.tags.map((t) => (
                <button
                  type="button"
                  key={t}
                  className="pill hover:opacity-70"
                  onClick={() => removeTag(t)}
                  title="Click to remove"
                  data-testid={`tag-pill-${t}`}
                >
                  #{t} ×
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addTag(); }
                }}
                placeholder="Add a tag and press Enter"
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)]"
                data-testid="vault-tag-input"
              />
              <Button type="button" variant="outline" onClick={addTag} className="rounded-xl" data-testid="vault-add-tag-btn">Add</Button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Secure notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => update({ notes: e.target.value })}
              placeholder="Encrypted at rest — anything you'd like to keep private."
              rows={3}
              className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] mt-1 font-mono text-sm"
              data-testid="vault-notes-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} data-testid="vault-cancel-btn">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
              data-testid="vault-save-btn"
            >
              {saving ? "Saving…" : item?.id ? "Save changes" : "Save to vault"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
